/**
 * DIE EINE Client-Quelle für neue Angebotsnummern.
 *
 * Früher würfelten sechs Erzeugungsstellen (store.ts ×3, home.tsx,
 * quote-sidebar.tsx, mobile-app.tsx ×3) unabhängig Zufallsnummern — mit
 * Duplikaten in Ollis Produktions-DB als Folge. Jetzt läuft JEDE Vergabe
 * über den serverseitigen Nummernkreis (/api/quote-number-circle), der
 * fortlaufend zählt und vergebene Nummern überspringt.
 *
 * Fehlerfall (Server nicht erreichbar): null — der Aufrufer lässt das Feld
 * leer bzw. nutzt einen deterministischen "Entwurf"-Platzhalter. Es wird NIE
 * wieder eine Zufallsnummer erzeugt.
 */

export interface QuoteNumberCircleStatus {
  prefix: string;
  yearMode: 'none' | 'two' | 'four';
  separator: string;
  digits: number;
  year: number;
  nextSeq: number;
  configured: boolean;
  preview: string;
}

export interface QuoteNumberCircleSuggestion {
  prefix: string;
  yearMode: 'none' | 'two' | 'four';
  separator: string;
  digits: number;
  nextSeq: number;
  preview: string;
  sampleCount: number;
  sample: string;
}

export interface NachtragBase {
  slot: number;
  offerNumber: string;
  timestamp?: string;
}

/** Nächste freie Angebotsnummer aus dem Nummernkreis reservieren. */
export async function requestNextQuoteNumber(): Promise<string | null> {
  try {
    const response = await fetch('/api/quote-number-circle/next', { method: 'POST' });
    const result = await response.json();
    if (!response.ok || !result?.success) return null;
    const number = result?.data?.number;
    return typeof number === 'string' && number.trim() ? number : null;
  } catch {
    return null;
  }
}

/**
 * Nächste Nachtragsnummer <Basis>/N<n> zum Basisangebot reservieren.
 * baseSlot (Slot des Basisangebots) macht die Vergabe bei mehrfach vergebenen
 * Nummern eindeutig — der Zähler läuft dann je Angebot statt je Nummer.
 */
export async function requestNachtragNumber(baseNumber: string, baseSlot?: number): Promise<string | null> {
  try {
    const response = await fetch('/api/quote-number-circle/next-nachtrag', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(
        Number.isInteger(baseSlot) && (baseSlot as number) >= 1
          ? { baseNumber, baseSlot }
          : { baseNumber },
      ),
    });
    const result = await response.json();
    if (!response.ok || !result?.success) return null;
    const number = result?.data?.number;
    return typeof number === 'string' && number.trim() ? number : null;
  } catch {
    return null;
  }
}

export async function fetchQuoteNumberCircleStatus(): Promise<QuoteNumberCircleStatus | null> {
  try {
    const response = await fetch('/api/quote-number-circle');
    const result = await response.json();
    if (!response.ok || !result?.success) return null;
    return result.data ?? null;
  } catch {
    return null;
  }
}

export async function saveQuoteNumberCircle(input: {
  prefix: string;
  yearMode: 'none' | 'two' | 'four';
  separator: string;
  digits: number;
  nextSeq: number;
}): Promise<{ ok: true; status: QuoteNumberCircleStatus } | { ok: false; error: string }> {
  try {
    const response = await fetch('/api/quote-number-circle', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    const result = await response.json();
    if (!response.ok || !result?.success) {
      return { ok: false, error: result?.error || `HTTP ${response.status}` };
    }
    return { ok: true, status: result.data };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : 'Netzwerkfehler' };
  }
}

/** Format-Vorschlag aus dem Bestand — wird NIE still übernommen, nur angezeigt. */
export async function fetchQuoteNumberSuggestion(): Promise<QuoteNumberCircleSuggestion | null> {
  try {
    const response = await fetch('/api/quote-number-circle/suggestion');
    const result = await response.json();
    if (!response.ok || !result?.success) return null;
    return result?.data?.suggestion ?? null;
  } catch {
    return null;
  }
}

/** Basisangebot zu einem Softec-Projekt (Brücken-Vertrag mode=nachtrag). */
export async function fetchNachtragBase(softecProjectId: string): Promise<NachtragBase | null> {
  try {
    const response = await fetch(
      `/api/quote-number-circle/nachtrag-base?softecProjectId=${encodeURIComponent(softecProjectId)}`,
    );
    const result = await response.json();
    if (!response.ok || !result?.success) return null;
    return result?.data?.base ?? null;
  } catch {
    return null;
  }
}
